'use client';

import Link from 'next/link';

import { cn } from '@ps/ui/utils';
import { BellOff, Lock, Radio } from 'lucide-react';

import { useCommunityContext } from '../../contexts/community/useCommunityContext';
import { formatChannelTimestamp } from '../../functions/utils/formatChannelTimestamps';
import { getLastMessagePreview } from '../../functions/utils/getLastMessagePreview';
import { ChannelVisibility } from '../../types/ChannelVisibility';
import type { CommunityChannel } from '../../types/CommunityChannel';
import { ChannelAvatar } from '../common/ChannelAvatar';

interface Props {
  channel: CommunityChannel;
  active?: boolean;
  /**
   * Whether mute is on for the channel. Slice 1 doesn't have a
   * notification-settings UI yet, so this is always `false` — the
   * prop is here so the bell-slash glyph from the design is wired
   * for when the next slice flips it on.
   */
  muted?: boolean;
}

/**
 * One row in the community channels list. Renders as a Next `<Link>`
 * pointing at `…/community/channels/[slug]` so each channel is a
 * deep-linkable URL.
 */
export function ChannelListItem({ channel, active, muted }: Props) {
  const { basePath } = useCommunityContext();
  const time = formatChannelTimestamp(
    channel.lastMessage?.createdAt ?? channel.updatedAt,
  );
  const preview = getLastMessagePreview(channel.lastMessage);
  const isPrivate = channel.visibility === ChannelVisibility.Private;

  return (
    <Link
      href={`${basePath}/${channel.slug}`}
      aria-current={active ? 'true' : undefined}
      className={cn(
        'flex w-full items-start gap-3 rounded-md px-3 py-3 text-left transition',
        active ? 'bg-surface-1' : 'hover:bg-surface-1/60',
      )}
    >
      <ChannelAvatar src={channel.icon} name={channel.name} size={44} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-1.5">
            <span className="text-content-1 min-w-0 truncate text-sm font-semibold">
              {channel.name}
            </span>
            {channel.isBroadcastChannel ? (
              <Radio
                className="text-content-3 h-3.5 w-3.5 shrink-0"
                aria-label="Broadcast channel"
              />
            ) : null}
            {isPrivate ? (
              <Lock
                className="text-content-3 h-3.5 w-3.5 shrink-0"
                aria-label="Private channel"
              />
            ) : null}
            {muted ? (
              <BellOff
                className="text-content-3 h-3.5 w-3.5 shrink-0"
                aria-label="Muted"
              />
            ) : null}
          </div>
          {time ? (
            <span className="text-content-3 shrink-0 text-[11px] tabular-nums">
              {time}
            </span>
          ) : null}
        </div>
        <p className="text-content-2 mt-1 text-xs">
          {channel.messageCount} Posts · {channel.memberCount} Members
        </p>
        {preview ? (
          <p className="text-content-3 mt-1 line-clamp-1 text-xs">{preview}</p>
        ) : null}
      </div>
    </Link>
  );
}
